import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

import { m } from '../shared';

// Live vitals over the client capture (the design's `dlVitals`): the capture is a flat image, so an SVG laid over it
// in the capture's own pixel space (1600×900) hides the Performance readouts under patches in the capture's colours
// and redraws them live. Every ~1–1.5 s the values drift to a new plausible reading (CPU 4–22 %, GPU 12–38 %,
// RAM 52–60 GB) with a short eased transition; temperatures trail their load and their markers ride the heat bar.
// Built once the capture has loaded, runs only while it is on screen and the tab is visible; reduced motion leaves
// the image as shot. Decorative: aria-hidden.

interface Gauge {
  id: 'cpu' | 'gpu';
  cx: number;
  cy: number;
  arc: string;
  ink: string;
  v: number;
  t: number;
  disc: string[];
  card: string;
  tempX: number;
  markX: number;
  markY: number;
}

const GAUGES: Gauge[] = [
  { id: 'cpu', cx: 363.4, cy: 777.7, arc: '#afef60', ink: '#aeee5e', v: 6, t: 42, disc: ['739:#384a28', '745:#334525', '751:#304125', '757:#2d3c24', '763:#283521', '769:#273120', '775:#222b1e', '781:#1f271f', '784:#1d251d'], card: '#16181b', tempX: 515.5, markX: 543, markY: 753 },
  { id: 'gpu', cx: 827, cy: 777.2, arc: '#56d609', ink: '#55d607', v: 16, t: 34, disc: ['739:#224113', '745:#1f4114', '751:#213c14', '757:#203716', '763:#1d3418', '769:#1a3017', '775:#1b2a17', '781:#1a2516', '784:#192416'], card: '#161a1d', tempX: 979.5, markX: 1007.8, markY: 765 },
];
const RADIUS = 64.1;
const TRACK = '#3b4246';
const PILL_OFF = [74, 79, 85];
const PILL_ON = ['#4ede00', '#60d805', '#63da00', '#6ed700', '#7dd700', '#86d600', '#8dd900', '#9bd600', '#a9d200', '#b4cf00'].map((hex) => [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)]);
const RAM0 = 55;
const PILLS = Array.from({ length: 10 }, (_unused, index) => index);

type Reading = { cpu: number; cpuT: number; gpu: number; gpuT: number; ram: number };
const BASE: Reading = { cpu: 6, cpuT: 42, gpu: 16, gpuT: 34, ram: RAM0 };

const point = (gauge: Gauge, deg: number) => {
  const angle = (deg * Math.PI) / 180;
  return `${(gauge.cx + RADIUS * Math.cos(angle)).toFixed(2)} ${(gauge.cy + RADIUS * Math.sin(angle)).toFixed(2)}`;
};
const arcPath = (gauge: Gauge) => `M${point(gauge, 150)} A${RADIUS} ${RADIUS} 0 1 1 ${point(gauge, 30)}`;
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const wobble = (amp: number) => (Math.random() - 0.5) * 2 * amp;
const easeInOutCubic = (progress: number) => (progress < 0.5 ? 4 * progress * progress * progress : 1 - (-2 * progress + 2) ** 3 / 2);

function nextReading(current: Reading): Reading {
  // loads wander around a gaming-idle mean with the odd burst; heat trails its load (lagged)
  const cpu = clamp(Math.random() < 0.12 ? 15 + Math.random() * 7 : current.cpu + wobble(5) + (9 - current.cpu) * 0.3, 4, 22);
  const gpu = clamp(Math.random() < 0.12 ? 30 + Math.random() * 8 : current.gpu + wobble(7) + (22 - current.gpu) * 0.3, 12, 38);
  return {
    cpu,
    gpu,
    cpuT: clamp(current.cpuT + (40 + ((cpu - 4) / 18) * 7 + wobble(0.6) - current.cpuT) * 0.45, 40, 47),
    gpuT: clamp(current.gpuT + (33 + ((gpu - 12) / 26) * 6 + wobble(0.5) - current.gpuT) * 0.45, 33, 39),
    ram: clamp(current.ram + wobble(1.1) + (56 - current.ram) * 0.15, 52, 60),
  };
}

export function ClientVitals({ cropRef, imgRef }: { cropRef: RefObject<HTMLDivElement>; imgRef: RefObject<HTMLImageElement> }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [built, setBuilt] = useState(false);

  useEffect(() => {
    const crop = cropRef.current;
    const img = imgRef.current;
    if (!crop || !img || !('IntersectionObserver' in window) || typeof window.requestAnimationFrame !== 'function') return undefined;
    const motionQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    let inView = false;
    let running = false;
    let raf = 0;
    let timer = 0;
    let startedAt = 0;
    let current: Reading = { ...BASE };
    let from: Reading = { ...BASE };
    let to: Reading = { ...BASE };
    const last = new Map<string, string>();

    const set = (key: string, attr: string, value: string) => {
      const cacheKey = `${key}:${attr}`;
      if (last.get(cacheKey) === value) return;
      const element = svgRef.current?.querySelector(`[data-k="${key}"]`);
      if (!element) return;
      last.set(cacheKey, value);
      if (attr === 'text') element.textContent = value;
      else element.setAttribute(attr, value);
    };
    const paint = () => {
      GAUGES.forEach((gauge) => {
        const value = current[gauge.id];
        const temp = current[`${gauge.id}T`];
        const dash = `${value.toFixed(2)} 200`;
        set(`${gauge.id}Arc`, 'stroke-dasharray', dash);
        set(`${gauge.id}Glow`, 'stroke-dasharray', dash);
        set(`${gauge.id}Use`, 'text', String(Math.round(value)));
        set(`${gauge.id}Temp`, 'text', `${Math.round(temp)}°C`);
        set(`${gauge.id}Mark`, 'transform', `translate(0 ${(-(temp - gauge.t) * 1.5).toFixed(2)})`);
      });
      set('ramGb', 'text', String(Math.round(current.ram)));
      const lit = (current.ram / 128) * 20 + 0.4; // +.4: the shot's 55 GB shows nine pills fully lit
      PILLS.forEach((index) => {
        const amount = clamp(lit - index, 0, 1);
        const on = PILL_ON[index] as number[];
        set(`pill${index}`, 'fill', `rgb(${PILL_OFF.map((channel, part) => Math.round(channel + ((on[part] ?? channel) - channel) * amount)).join(',')})`);
      });
    };
    const frame = (now: number) => {
      if (!startedAt) startedAt = now;
      const progress = Math.min(1, (now - startedAt) / 700);
      const eased = easeInOutCubic(progress);
      current = {
        cpu: from.cpu + (to.cpu - from.cpu) * eased,
        cpuT: from.cpuT + (to.cpuT - from.cpuT) * eased,
        gpu: from.gpu + (to.gpu - from.gpu) * eased,
        gpuT: from.gpuT + (to.gpuT - from.gpuT) * eased,
        ram: from.ram + (to.ram - from.ram) * eased,
      };
      paint();
      raf = progress < 1 ? window.requestAnimationFrame(frame) : 0;
    };
    const step = () => {
      to = nextReading(current);
      from = { ...current };
      startedAt = 0;
      if (raf) window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(frame);
      timer = window.setTimeout(step, 1000 + Math.random() * 500);
    };
    const stop = () => {
      running = false;
      if (raf) window.cancelAnimationFrame(raf);
      window.clearTimeout(timer);
      raf = 0;
      timer = 0;
    };
    const sync = () => {
      const reduced = !!motionQuery?.matches;
      const go = inView && !document.hidden && !reduced && img.complete && img.naturalWidth > 0;
      if (go && !running) {
        setBuilt(true);
        running = true;
        timer = window.setTimeout(step, 400);
      } else if (!go && running) stop();
      if (reduced) {
        stop();
        current = { ...BASE };
        last.clear();
        paint();
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          inView = entry.isIntersecting;
        });
        sync();
      },
      { threshold: 0.15 }
    );
    observer.observe(crop);
    document.addEventListener('visibilitychange', sync);
    img.addEventListener('load', sync);
    motionQuery?.addEventListener?.('change', sync);

    return () => {
      stop();
      observer.disconnect();
      document.removeEventListener('visibilitychange', sync);
      img.removeEventListener('load', sync);
      motionQuery?.removeEventListener?.('change', sync);
    };
  }, [cropRef, imgRef]);

  if (!built) return null;

  return (
    <svg ref={svgRef} className={m('dl__vitals')} xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 900' preserveAspectRatio='xMidYMid slice' aria-hidden='true' focusable='false'>
      <defs>
        {GAUGES.map((gauge) => (
          <linearGradient key={gauge.id} id={`dlv-${gauge.id}-disc`} gradientUnits='userSpaceOnUse' x1='0' y1='739' x2='0' y2='784'>
            {gauge.disc.map((stop) => {
              const [offset, color] = stop.split(':');
              return <stop key={stop} offset={((Number(offset) - 739) / 45).toFixed(3)} stopColor={color} />;
            })}
          </linearGradient>
        ))}
        <filter id='dlv-soft' x='-20%' y='-30%' width='140%' height='160%' colorInterpolationFilters='sRGB'>
          <feGaussianBlur stdDeviation='2' />
        </filter>
      </defs>
      {GAUGES.map((gauge) => {
        const d = arcPath(gauge);
        const x0 = gauge.cx - 36;
        return (
          <g key={gauge.id}>
            <path d={d} fill='none' stroke={TRACK} strokeWidth='9.6' />
            <path data-k={`${gauge.id}Glow`} d={d} pathLength={100} fill='none' stroke={gauge.arc} strokeOpacity='.2' strokeWidth='15' strokeLinecap='round' strokeDasharray={`${gauge.v} 200`} />
            <path data-k={`${gauge.id}Arc`} d={d} pathLength={100} fill='none' stroke={gauge.arc} strokeWidth='8.6' strokeLinecap='round' strokeDasharray={`${gauge.v} 200`} />
            <rect x={x0.toFixed(1)} y='739' width='64' height='42.5' fill={`url(#dlv-${gauge.id}-disc)`} filter='url(#dlv-soft)' />
            <text data-k={`${gauge.id}Use`} className={m('dlv-use')} x={(gauge.cx - 4).toFixed(1)} y='775.2' textAnchor='middle' fill={gauge.ink}>
              {gauge.v}
            </text>
            <rect x={gauge.tempX - 60} y='652' width='63' height='31' fill={gauge.card} filter='url(#dlv-soft)' />
            <text data-k={`${gauge.id}Temp`} className={m('dlv-temp')} x={gauge.tempX} y='675.5' textAnchor='end' fill='#aeb2b6'>
              {`${gauge.t}°C`}
            </text>
            <rect x={gauge.markX} y={gauge.markY - 16} width='16' height='36' fill={gauge.card} />
            <g data-k={`${gauge.id}Mark`}>
              <path d={`M${gauge.markX + 0.6} ${gauge.markY}l8.6 -6.8v13.6z`} fill='#fff' stroke='#fff' strokeWidth='1.6' strokeLinejoin='round' className={m('dlv-mark')} />
            </g>
          </g>
        );
      })}
      <rect x='1381' y='715' width='41' height='32' fill='#171c1c' filter='url(#dlv-soft)' />
      <text data-k='ramGb' className={m('dlv-gb')} x='1419' y='742.6' textAnchor='end' fill='#c5f5a4'>
        {RAM0}
      </text>
      <rect x='1086' y='744' width='151' height='26' fill='#161c1a' />
      {PILLS.map((index) => (
        <rect key={index} data-k={`pill${index}`} x={1090.2 + 15 * index} y='748.2' width='7.4' height='17.2' rx='3.7' fill={`rgb(${PILL_OFF.join(',')})`} />
      ))}
    </svg>
  );
}
